import React, { useEffect, useMemo, useState } from 'react';
import { Wallet, CheckCircle2, CalendarCheck, Copy, Check, CreditCard } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatTime, formatMoney } from '../i18n';
import { Card, Badge, Alert, Spinner, EmptyState, Button } from '../components/ui';
import { callServer } from '../lib/serverCall';

/**
 * What a patient owes and has paid, with each professional (migration 0046).
 *
 * Everything comes from my_balance(), which returns the patient's own part
 * only: sessions that already happened and are not paid, the payments that
 * stand, and the sessions ahead a payment already covers. A session ahead never
 * carries a price, and nothing here says what an obra social pays the
 * professional.
 *
 * A professional who has not turned balances on in Configuración still appears,
 * with a sentence instead of figures: a missing card would read as "nothing
 * owed", which nobody has said.
 *
 * Grouped by person, then by professional. The holder of an account sees the
 * minors in their care, and each professional's money is separate, as in Pagos.
 */

const Section = ({ title, children }) => (
  <div>
    <h3 className="mb-2 text-sm font-semibold text-slate-800">{title}</h3>
    {children}
  </div>
);

/**
 * Where to transfer (0062): the professional's alias or CBU/CVU and the holder's
 * name, so the patient can check who they are paying. The copy button saves
 * retyping 22 digits; the text stays selectable if the browser refuses it.
 */
const TransferDetails = ({ alias, holder }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(alias);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (copyError) {
      setCopied(false);
    }
  };

  return (
    <Section title={t('myPayments.transferTitle')}>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 p-3 text-sm">
        <div className="min-w-0">
          <p className="break-all font-mono font-medium text-slate-900">{alias}</p>
          {holder && <p className="text-slate-600">{t('myPayments.transferHolder', { holder })}</p>}
        </div>
        <Button variant="secondary" onClick={copy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {t(copied ? 'myPayments.copied' : 'myPayments.copy')}
        </Button>
      </div>
    </Section>
  );
};

const Balance = ({ entry }) => {
  // Before any return, as hooks must be.
  const owesSomething = Boolean(entry.shown) && Number(entry.owed_total) > 0;

  // Offered only when this professional takes Mercado Pago (0074). The link is
  // for exactly what is owed: the server works it out, not this page.
  const [takesMercadoPago, setTakesMercadoPago] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  useEffect(() => {
    if (!owesSomething) return;
    supabase.rpc('takes_mercadopago', { p_therapist_id: entry.therapist_id })
      .then(({ data }) => setTakesMercadoPago(Boolean(data)));
  }, [owesSomething, entry.therapist_id]);

  const payOnline = async () => {
    setPaying(true);
    setPayError(null);
    try {
      const { url } = await callServer('mercadopago-link', {
        dependent_id: entry.dependent_id, therapist_id: entry.therapist_id,
      });
      window.location.assign(url);
    } catch (failure) {
      setPayError(failure.message);
      setPaying(false);
    }
  };  if (!entry.shown) {
    return (
      <Card title={entry.professional}>
        <p className="text-sm text-slate-600">{t('myPayments.notShown', { professional: entry.professional })}</p>
      </Card>
    );
  }

  const owes = Number(entry.owed_total) > 0;
  // Money since 0072; a count of sessions before it.
  const credit = Number(entry.credit_amount ?? 0);

  return (
    <Card
      title={entry.professional}
      actions={owes ? (
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-slate-500">{t('myPayments.owes')}</p>
          <p className="text-xl font-bold tabular-nums text-rose-700">{formatMoney(entry.owed_total)}</p>
        </div>
      ) : (
        <Badge tone="success"><CheckCircle2 size={12} />{t('myPayments.upToDate')}</Badge>
      )}
    >
      <div className="space-y-5">
        {entry.owed.length > 0 && (
          <Section title={t('myPayments.owedTitle')}>
            <ul className="divide-y divide-slate-100">
              {entry.owed.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span>
                    <span className="inline-block font-medium text-slate-800 first-letter:uppercase">
                      {formatDate(row.starts_at, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-slate-500"> · {t(`myPayments.kinds.${row.status}`)}</span>
                    {/* Part paid: what it still lacks is what is owed, and the
                        patient sees that some of it arrived (0072). */}
                    {row.partial && (
                      <span className="text-amber-700">
                        {' · '}{t('myPayments.partial', { price: formatMoney(row.price) })}
                      </span>
                    )}
                  </span>
                  <span className="font-medium tabular-nums text-slate-900">{formatMoney(row.amount)}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {owes && takesMercadoPago && (
          <div className="space-y-2">
            <Button onClick={payOnline} loading={paying}>
              <CreditCard size={16} />{t('mercadopago.payButton')}
            </Button>
            <p className="text-xs text-slate-500">
              {t('mercadopago.payHint', { professional: entry.professional })}
            </p>
            {payError && <Alert tone="danger">{payError}</Alert>}
          </div>
        )}

        {entry.payment_alias && (
          <TransferDetails alias={entry.payment_alias} holder={entry.payment_holder} />
        )}

        {(entry.prepaid.length > 0 || credit > 0) && (
          <Section title={t('myPayments.prepaidTitle')}>
            {entry.prepaid.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {entry.prepaid.map((row) => (
                  <li key={row.id}>
                    <Badge tone={row.partial ? 'warning' : 'success'}>
                      <CalendarCheck size={12} />
                      <span className="first-letter:uppercase">
                        {formatDate(row.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                      {formatTime(row.starts_at)}
                      {row.partial && ` · ${t('myPayments.partialAhead')}`}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            {credit > 0 && (
              <p className={`text-sm text-slate-600 ${entry.prepaid.length ? 'mt-2' : ''}`}>
                {t('myPayments.credit', { amount: formatMoney(credit) })}
              </p>
            )}
          </Section>
        )}

        <Section title={t('myPayments.paymentsTitle')}>
          {entry.payments.length === 0 ? (
            <p className="text-sm text-slate-500">{t('myPayments.noPayments')}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {entry.payments.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span>
                    <span className="font-medium text-slate-800">{formatDate(row.paid_at)}</span>
                    <span className="text-slate-500">
                      {row.method && ` · ${t(`payments.methods.${row.method}`)}`}
                    </span>
                  </span>
                  <span className="font-medium tabular-nums text-emerald-700">{formatMoney(row.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </Card>
  );
};

const MyPaymentsView = () => {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.rpc('my_balance').then(({ data, error: loadError }) => {
      if (loadError) setError(loadError.message);
      setEntries(data ?? []);
    });
  }, []);

  const people = useMemo(() => {
    const byPerson = new Map();
    for (const entry of entries ?? []) {
      if (!byPerson.has(entry.dependent_id)) {
        byPerson.set(entry.dependent_id, {
          id: entry.dependent_id, name: `${entry.first_name} ${entry.last_name}`, entries: [],
        });
      }
      byPerson.get(entry.dependent_id).entries.push(entry);
    }
    return [...byPerson.values()];
  }, [entries]);

  // What Mercado Pago said on the way back, if the patient just paid there.
  // The payment itself is recorded when Mercado Pago tells the server, which can
  // take a moment: this only says it is on its way.
  const returnedStatus = new URLSearchParams(window.location.search).get('status');

  if (!entries) return <Spinner />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('myPayments.title')}</h1>
        <p className="text-sm text-slate-500">{t('myPayments.subtitle')}</p>
      </div>

      {returnedStatus === 'approved' && <Alert tone="success">{t('mercadopago.returnedApproved')}</Alert>}
      {(returnedStatus === 'pending' || returnedStatus === 'in_process') && (
        <Alert tone="info">{t('mercadopago.returnedPending')}</Alert>
      )}

      {error && <Alert tone="danger" title={t('home.loadFailed')}>{error}</Alert>}

      {!error && people.length === 0 && (
        <Card><EmptyState icon={Wallet} title={t('myPayments.none')} /></Card>
      )}

      {people.map((person) => (
        <section key={person.id} className="space-y-3">
          {/* Only a family needs the name: alone, every card is the reader's own. */}
          {people.length > 1 && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{person.name}</h2>
          )}
          {person.entries.map((entry) => <Balance key={entry.therapist_id} entry={entry} />)}
        </section>
      ))}

      {people.some((person) => person.entries.some((entry) => entry.shown)) && (
        <p className="text-xs text-slate-500">{t('myPayments.hint')}</p>
      )}
    </div>
  );
};

export default MyPaymentsView;
